/**
 * Cookie-based storage adapter for Supabase Auth.
 *
 * When multiple frontends share a parent domain (e.g. admin.faces.com,
 * onboarding.faces.com) this adapter stores the Supabase session in a
 * cookie scoped to the parent domain, enabling seamless SSO across all
 * subdomains.
 *
 * Falls back to localStorage when no cookie domain is configured (local dev).
 */

export interface CookieStorageOptions {
  /** Parent domain for the cookie, e.g. '.faces.com'. Omit for localhost. */
  cookieDomain?: string;
  /** Cookie path. Defaults to '/'. */
  cookiePath?: string;
  /** Secure flag. Defaults to true when cookieDomain is set. */
  secure?: boolean;
  /** SameSite attribute. Defaults to 'lax'. */
  sameSite?: 'strict' | 'lax' | 'none';
  /** Max-age in seconds. Defaults to 365 days. */
  maxAge?: number;
}

const DEFAULT_MAX_AGE = 365 * 24 * 60 * 60; // 365 days

function parseCookies(): Record<string, string> {
  if (typeof document === 'undefined') return {};
  const pairs = document.cookie.split(';');
  const result: Record<string, string> = {};
  for (const pair of pairs) {
    const idx = pair.indexOf('=');
    if (idx === -1) continue;
    const key = pair.slice(0, idx).trim();
    const value = pair.slice(idx + 1).trim();
    try {
      result[key] = decodeURIComponent(value);
    } catch {
      result[key] = value;
    }
  }
  return result;
}

function setCookie(
  key: string,
  value: string,
  opts: Required<Pick<CookieStorageOptions, 'cookiePath' | 'secure' | 'sameSite' | 'maxAge'>> & { cookieDomain?: string }
) {
  if (typeof document === 'undefined') return;
  let cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  cookie += `; path=${opts.cookiePath}`;
  cookie += `; max-age=${opts.maxAge}`;
  cookie += `; samesite=${opts.sameSite}`;
  if (opts.secure) cookie += '; secure';
  if (opts.cookieDomain) cookie += `; domain=${opts.cookieDomain}`;
  document.cookie = cookie;
}

function deleteCookie(
  key: string,
  opts: { cookiePath: string; cookieDomain?: string }
) {
  if (typeof document === 'undefined') return;
  let cookie = `${encodeURIComponent(key)}=; path=${opts.cookiePath}; max-age=0`;
  if (opts.cookieDomain) cookie += `; domain=${opts.cookieDomain}`;
  document.cookie = cookie;
}

/**
 * Creates a storage adapter compatible with Supabase Auth's `storage` option.
 * Uses cookies for cross-subdomain SSO when cookieDomain is provided,
 * otherwise falls back to localStorage.
 */
export function createCookieStorage(options: CookieStorageOptions = {}) {
  const {
    cookieDomain,
    cookiePath = '/',
    secure = !!cookieDomain,
    sameSite = 'lax',
    maxAge = DEFAULT_MAX_AGE,
  } = options;

  // If no cookieDomain, use localStorage (local dev / single domain)
  if (!cookieDomain) {
    return {
      getItem: (key: string): string | null => {
        if (typeof window === 'undefined') return null;
        return localStorage.getItem(key);
      },
      setItem: (key: string, value: string): void => {
        if (typeof window === 'undefined') return;
        localStorage.setItem(key, value);
      },
      removeItem: (key: string): void => {
        if (typeof window === 'undefined') return;
        localStorage.removeItem(key);
      },
    };
  }

  // Cookie-based storage for cross-subdomain SSO
  const cookieOpts = { cookieDomain, cookiePath, secure, sameSite, maxAge };

  return {
    getItem: (key: string): string | null => {
      const cookies = parseCookies();
      return cookies[key] ?? null;
    },
    setItem: (key: string, value: string): void => {
      setCookie(key, value, cookieOpts);
    },
    removeItem: (key: string): void => {
      deleteCookie(key, { cookiePath, cookieDomain });
    },
  };
}

/**
 * Resolves the cookie domain from the current hostname.
 * e.g. admin.faces.com → .faces.com
 *      localhost → undefined (falls back to localStorage)
 *      faces.com → .faces.com
 */
export function resolveCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const hostname = window.location.hostname;

  // No cookies for localhost or IP addresses
  if (hostname === 'localhost' || hostname === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return undefined;
  }

  // For Vercel preview deployments (*.vercel.app) — no shared cookies
  if (hostname.endsWith('.vercel.app')) {
    return undefined;
  }

  // Extract parent domain: admin.faces.com → .faces.com
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    return '.' + parts.slice(-2).join('.');
  }

  return undefined;
}
